interface CacheParams {
  cacheName: string;
  endpoint: string;
}

function updateServiceWorkerCache<T>({cacheName, endpoint}: CacheParams) {
  return async (updateBody: (cachedBody: T) => T) => {
    try {
      // find the correct cache entry
      const cacheGroup = await caches.open(cacheName);
      const cacheKeys = await cacheGroup.keys();
      const cachedEndpointKey = cacheKeys.find(
        (cache) => cache.url === endpoint,
      );

      if (!cachedEndpointKey) {
        const initialBody = updateBody({} as T);
        const initialResponse = new Response(JSON.stringify(initialBody), {
          status: 200,
          statusText: 'OK',
          headers: {'Content-Type': 'application/json'},
        });

        await cacheGroup.put(new Request(endpoint), initialResponse);
        return Promise.resolve(initialBody);
      }

      // get the cached response
      const cachedRequest = new Request(cachedEndpointKey);
      const cachedResponse = await cacheGroup.match(cachedRequest);

      if (!cachedResponse) {
        throw new Error('Cached response not found');
      }

      // expose the cached data to be edited and convert the response to correct cache format
      const cachedBody = await cachedResponse.clone().text();
      const updatedBody = updateBody(JSON.parse(cachedBody));
      const updatedCache = new Response(JSON.stringify(updatedBody), {
        status: cachedResponse.status,
        statusText: cachedResponse.statusText,
        headers: new Headers(cachedResponse.headers),
      });

      if (!updatedCache) {
        throw new Error('Unable to transform data into cache format');
      }

      // update the cache
      await cacheGroup.put(cachedEndpointKey, updatedCache);
      return Promise.resolve(updatedBody);
    } catch (error: any) {
      return Promise.reject(new Error(error?.message));
    }
  };
}

export default updateServiceWorkerCache;
